<template>
    <div v-if="isLoading" class="mb-5">
        <font-awesome-icon icon="spinner" spin/>
    </div>
    <div v-else>
        <!-- SWITCH CARRIER MODAL -->
        <b-modal id="switchCarrierModal"
                 title="Wijzig vervoerder en/of bezorgwijze"
                 size="md"
                 centered
                 dialog-class="shadow-none"
                 @ok="changeCarrier"
        >
            <b-row class="mb-2">
                <b-col cols="4">Vervoerder:</b-col>
                <b-col cols="8">
                    <span v-if="!isOnlyOneCarrierAvailable">
                        <div v-for="carrier in carriers" :key="carrier.identifier">
                            <input name="carrier" type="radio" :id="carrier.identifier" :value="carrier.identifier" v-model.trim="selectedCarrier" class="mr-2">
                            <label :for="carrier.identifier">{{ carrier.display_name }}</label>
                        </div>
                    </span>
                    <span v-else>{{selectedCarrierData.display_name}}</span>
                </b-col>
            </b-row>
            <b-row>
                <b-col cols="4">Bezorgwijze:</b-col>
                <b-col cols="8">
                    <span v-if="!isOnlyOneCarrierMethodAvailable">
                        <div v-for="carrierMethod in carrierMethods" :key="carrierMethod.identifier">
                            <input name="carrier_method" type="radio" :id="carrierMethod.identifier" :value="carrierMethod.identifier" v-model.trim="selectedCarrierMethod" class="mr-2">
                            <label :for="carrierMethod.identifier">{{ carrierMethod.display_name }}</label>
                        </div>
                    </span>
                    <span v-else>{{selectedCarrierMethodData.display_name}}</span>
                </b-col>
            </b-row>
            <template #modal-footer="{ cancel, ok }">
                <b-btn variant="secondary" :disabled="isSavingCarrier" @click="cancel">Annuleren</b-btn>
                <b-btn variant="primary" :disabled="isSavingCarrier || selectedCarrierMethod === dispatchRequest.carrier_method.identifier" @click="changeCarrier">
                    <span v-if="!isSavingCarrier">OK</span>
                    <font-awesome-icon v-else icon="spinner" spin/>
                </b-btn>
            </template>
        </b-modal>

        <!-- UNABLE TO SHIP REASON MODAL -->
        <b-modal id="unableToShipModal"
                 title="Kan niet leveren"
                 size="md"
                 centered
                 dialog-class="shadow-none"
        >
            <template>
                <b-row>
                    <b-col cols="3" class="pt-0">Reden:</b-col>
                    <b-col cols="9">
                        <div v-for="(display_name, identifier) in unableToShipReasons" :key="identifier">
                            <input name="reason" type="radio" :id="identifier" :value="identifier" v-model.trim="unableToShipSelectedReason" v-html="display_name" class="mr-2">
                            <label :for="identifier">{{ display_name }}</label>
                        </div>
                    </b-col>
                </b-row>
            </template>
            <template #modal-footer="{ cancel }">
                <b-btn variant="secondary" :disabled="isSavingCarrier" @click="cancel">Annuleren</b-btn>
                <b-btn variant="danger" :disabled="isFinishing" @click.prevent="unableToShip">
                    <span v-if="!isFinishing">Kan niet leveren</span>
                    <font-awesome-icon v-else icon="spinner" spin/>
                </b-btn>
            </template>
        </b-modal>

        <!-- NORMAL PAGE -->
        <div class="row">
            <div class="col-12">
                <div v-if="isScannable && (!carriers.length)" class="alert alert-warning">
                    <strong>Let op:</strong>
                    Deze order heeft het type '{{dispatchRequest.dispatch_type.display_name}}'. Hierbij zijn geen vervoerders en/of bezorgwijzen geactiveerd.<br/>
                    Het is daardoor niet mogelijk deze order te voltooien.
                </div>
                <div v-else-if="isScannable && selectedCarrier && !carrierMethods.length && isOriginalCarrierMethodOverruleable" class="alert alert-warning">
                    <strong>Let op:</strong>
                    Deze order heeft als vervoerder '{{selectedCarrierData.display_name}}' geselecteerd. Hierbij zijn geen bezorgwijzen geactiveerd.<br />
                    Het is daardoor niet mogelijk deze order te voltooien.
                </div>
                <div v-else-if="dispatchRequest.paused_at" class="alert alert-warning">
                    <strong><font-awesome-icon icon="pause-circle" class="text-danger font-size-xlg align-middle mr-2"/></strong>
                    Deze levering is gepauzeerd! Het is niet mogelijk om deze te verwerken totdat deze weer wordt hervat.
                </div>
                <div class="card mb-2">
                    <div class="card-body">
                        <div class="card-title">
                            <span v-if="isScannable">SCANNEN VAN LEVERING {{ dispatchRequest.sales_order.identifier }}</span>
                            <span v-else>BEKIJK ORDER {{ dispatchRequest.sales_order.identifier }}</span>
                        </div>

                        <div class="row mb-2">
                            <div class="col-12 col-sm-6">

                                <b-table-simple small borderless>
                                    <colgroup>
                                        <col style="width:200px;"/>
                                        <col style="width:auto;"/>
                                    </colgroup>
                                    <b-tbody>
                                      <b-tr>
                                          <b-td><strong>Afleveringswijze:</strong></b-td>
                                          <b-td>{{dispatchRequest.dispatch_type.display_name}}</b-td>
                                      </b-tr>
                                      <b-tr>
                                          <b-td><strong>Vervoerder:</strong></b-td>
                                          <b-td>
                                              <carrier-icon :carrier="dispatchRequest.carrier_method.carrier.identifier" class="carrier-icon d-inline-block align-middle"/>
                                              {{ dispatchRequest.carrier_method.carrier.display_name }}
                                              <span :title="!isSwitchCarrierEnabled ? 'Wijzigen is niet mogelijk voor deze vervoerder/bezorgwijze' : ''">
                                                  <b-btn variant="link" size="sm" :disabled="!isSwitchCarrierEnabled" @click="$bvModal.show('switchCarrierModal')" class="py-0">wijzig</b-btn>
                                              </span>
                                          </b-td>
                                      </b-tr>

                                      <b-tr>
                                          <b-td><strong>Bezorgwijze:</strong></b-td>
                                          <b-td v-if="selectedCarrierMethodData.display_name">
                                              {{ selectedCarrierMethodData.display_name }}
                                              <span :title="!isSwitchCarrierEnabled ? 'Wijzigen is niet mogelijk voor deze vervoerder/bezorgwijze' : ''">
                                                  <b-btn variant="link" size="sm" :disabled="!isSwitchCarrierEnabled" @click="$bvModal.show('switchCarrierModal')" class="py-0">wijzig</b-btn>
                                              </span>
                                          </b-td>
                                          <b-td v-else>
                                              <b-alert show variant="warning" size="sm" class="mb-0">Onbekend. Mogelijk is de toegewezen bezorgwijze niet geactiveerd voor deze locatie.</b-alert>
                                          </b-td>
                                      </b-tr>
                                      <b-tr>
                                          <b-td><strong>Telefoonnummer klant:</strong></b-td>
                                          <b-td >{{billingAddress.phone_number}}</b-td>
                                      </b-tr>
                                    </b-tbody>
                                </b-table-simple>

                                <br/>
                                <span class="d-block d-sm-none float-right mt-n2">
                                    <span v-if="showBoxSuggestion(dispatchRequest)" class="mr-3">
                                      <strong style="font-size: 24px;">{{dispatchRequest.box_suggestion.suggested_box.identifier}}</strong>
                                    </span>
                                    <!-- Extra large print button for small screens -->
                                    <print-labels-button :dispatch-request="dispatchRequest" variant="outline-primary" size="xxl"/>
                                </span>
                                <div class="card-title">ORDERREGELS</div>
                            </div>
                            <div class="col-12 col-sm-6 text-right">
                                  <span class="d-none d-sm-block">
                                      <span v-if="showBoxSuggestion(dispatchRequest)" class="mr-1">
                                        <strong>{{dispatchRequest.box_suggestion.suggested_box.identifier}}</strong>
                                      </span>
                                      <!-- Default print & download buttons for normal screens -->
                                      orderbon
                                      <download-packing-slip-button :dispatch-request="dispatchRequest" variant="outline-primary" size="lg" :ask-label-count="false">Download</download-packing-slip-button>
                                      <download-packing-slip-button v-if="dispatchRequest.dispatch_type?.identifier !== 'click_and_collect'" :dispatch-request="dispatchRequest" variant="outline-primary" size="lg" :ask-label-count="true" class="ml-2">Extra labels aanvragen</download-packing-slip-button>
                                      <print-labels-button :dispatch-request="dispatchRequest" variant="outline-primary" size="lg" class="ml-2"/>
                                  </span>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-sm-12">
                                <div class="table-responsive">
                                    <b-table-simple stacked="sm" hover small striped class="mb-1 order-lines">
                                        <colgroup>
                                            <col style="width: 100px">
                                            <col style="width: auto">
                                            <col style="width: 15%">
                                            <col style="width: 15%">
                                            <col style="width: 15%">
                                        </colgroup>

                                        <b-thead>
                                          <b-tr>
                                              <th>Afbeelding</th>
                                              <th>Artikel</th>
                                              <th>
                                                  <abbr style="background:transparent;color:inherit;border-bottom:1px dotted #000;cursor:help" title="Aantal artikelen te leveren">Leveren</abbr>
                                              </th>
                                              <th v-if="isScannable">
                                                  <abbr style="background:transparent;color:inherit;border-bottom:1px dotted #000;cursor:help" title="Aantal artikelen gescand">Gescand</abbr>
                                              </th>
                                              <th v-if="isScannable">Alles gescand?</th>
                                          </b-tr>
                                        </b-thead>

                                        <b-tbody>
                                          <b-tr v-for="(dispatchRequestLine, index) in dispatchRequestLines"
                                              class="order-info-scan-line"
                                              :key="index"
                                          >
                                              <b-td stacked-heading="Afbeelding">
                                                  <img v-if="getProductImage(dispatchRequestLine.product_identifier)" :src="getProductImage(dispatchRequestLine.product_identifier)" alt="" class="product-image"
                                                      @click="$bvModal.show(`product_image_${dispatchRequestLine.product_identifier}`)"/>
                                                  <span v-else>
                                                    -
                                                  </span>
                                                  <b-modal :id="`product_image_${dispatchRequestLine.product_identifier}`" size="md"
                                                           :title="`${dispatchRequestLine.product_identifier} - ${dispatchRequestLine.product_name ?? dispatchRequestLine.sales_order_line.product_name} - ${dispatchRequestLine.unit_price}`"
                                                           :hide-footer="true"
                                                           @hidden="$refs['scanInput']?.focus()"
                                                  >
                                                      <img :src="getProductImage(dispatchRequestLine.product_identifier)" alt="" style="max-width: 100%;"/>
                                                  </b-modal>
                                              </b-td>

                                              <b-td valign="top" stacked-heading="Artikel">
                                                  <p class="mb-0" :data-eans="dispatchRequestLine.sales_order_line.ean_codes">{{ dispatchRequestLine.product_name ?? dispatchRequestLine.sales_order_line.product_name }}</p>
                                                  <p v-if="dispatchRequestLine.release_date" class="text-primary mb-0 font-weight-bold">Release datum: {{ dispatchRequestLine.release_date }}</p>
                                                  <p class="mb-0 font-11">
                                                      <product-number :number="dispatchRequestLine.product_identifier" :dispatch-request-line="dispatchRequestLine" link-class="text-secondary"/>
                                                  </p>
                                              </b-td>

                                              <b-td valign="top" stacked-heading="Leveren">
                                                  {{ dispatchRequestLine.quantity }}
                                              </b-td>

                                              <b-td v-if="isScannable" class="order-info-scan-quantity-scanned" valign="top" stacked-heading="Gescand">
                                                  {{ dispatchRequestLine.scanned }}
                                              </b-td>

                                              <b-td v-if="isScannable" class="order-info-scan-finished" stacked-heading="Alles gescand?">
                                                  <span class="iconCheckmark" v-if="dispatchRequestLine.scanned === dispatchRequestLine.quantity">&check;</span>
                                                  <span v-else>Nee</span>
                                              </b-td>
                                          </b-tr>
                                          <b-tr v-if="dispatchRequest.gift_wrapped" class="order-info-scan-line">
                                              <b-td>&nbsp;</b-td>
                                              <b-td><strong>Cadeaupapier</strong></b-td>
                                              <b-td v-if="isScannable">1</b-td>
                                              <b-td v-if="isScannable">&nbsp;</b-td>
                                          </b-tr>
                                        </b-tbody>
                                    </b-table-simple>
                                </div>
                            </div>
                        </div>

                        <div v-if="isScannable" class="row">
                            <div class="col-md-12 pb-0 pt-0">
                                <hr class="my-1">
                                <h6>Handmatig barcodes invoeren</h6>
                            </div>

                            <div class="col-md-4">
                                <form class="order-info-scan-line-form" @submit.prevent="scan">
                                    <div class="input-group mb-3">
                                        <b-input type="text" autofocus
                                               :disabled="isFinishing"
                                               v-model.trim="scanIdentifier"
                                               ref="scanInput"
                                               class="order-info-scan-line-input form-control mr-2"
                                               title="Scan barcode"/>

                                        <button class="btn btn-primary" type="submit" :disabled="isFinishing">Verzend EAN code</button>

                                    </div>
                                </form>
                            </div>
                        </div>

                        <b-row v-if="isScannable" class="mt-4">
                            <b-col cols="12">
                                <button type="button"
                                        @click.prevent="$bvModal.show('unableToShipModal')"
                                        :disabled="isFinishing"
                                        class="btn btn-danger mr-2">
                                    Kan niet leveren
                                </button>

                                <button type="button"
                                        @click.prevent="ship"
                                        :disabled="!canShip"
                                        class="btn btn-success">
                                    Voltooi levering
                                </button>
                            </b-col>
                        </b-row>

                        <div v-if="usesNewScanProcess">
                            <b-button variant="primary" @click="startNewScanProcess" :disabled="isCreatingBatch">
                                Start verwerking
                                <font-awesome-icon v-if="isCreatingBatch" icon="spinner" spin/>
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

</template>

<script>
import DownloadPackingSlipButton from "./components/DownloadPackingSlipButton";
import CarrierIcon from "../../components/CarrierIcon";
import OrderDetailOverview from "./Overview.vue";
import ProductNumber from "../../components/ProductNumber.vue";
import PrintLabelsButton from "@/views/OrderDetail/components/PrintLabelsButton.vue";
import {price} from "@/helpers/numbers";
import {today} from "@/helpers/date";
import {getLocationPropertyValue} from "@/helpers/locations";
const SCANNABLE_STATUSES = ['requested']

export default {
    name: "Scan",
    components: {PrintLabelsButton, ProductNumber, CarrierIcon, DownloadPackingSlipButton},
    props: {
        salesOrder: {required: true},
        dispatchRequestId: {required: true},
    },

    data() {
        return {
            isLoading: true,
            isCreatingBatch: false,
            isSavingCarrier: false,
            isFinishing: false,
            isFinishedUnsuccessfulDialogueOpened: false,
            isFinishedUnsuccessfulWithoutReason: false,
            isFinishedSuccessFullyMessageOpened: false,
            dispatchRequest: {},
            carriers: [],
            scannedIdentifiers: {},
            scanIdentifier: '',
            lastScanIdentifier: '',
            unableToShipSelectedReason: '',
            selectedCarrier: '',
            selectedCarrierMethod: '',
            unableToShipReasons: {},
        }
    },

    computed: {
        today: today,
        isScannable() {
            if (this.usesNewScanProcess)
                return false

            return SCANNABLE_STATUSES.indexOf(this.dispatchRequest.status) !== -1
                && !this.dispatchRequest.paused_at
        },
        usesNewScanProcess() {
            let dispatcherLocation = this.$store.state.locationsForUser.find(l => l.identifier === this.dispatchRequest.dispatcher_identifier)
            return !!dispatcherLocation.location_properties['pickByStore.useNewProductProcessing']
        },
        isPackingSlipDownloadable() {
            return this.dispatchRequest.dispatch_type && this.dispatchRequest.carrier_identifier !== 'unknown'
        },
        isOnlyOneCarrierAvailable() {
            return this.carriers.length === 1
        },
        isOnlyOneCarrierMethodAvailable() {
            return this.carrierMethods.length === 1
        },
        canShip() {
            return (
                this.isEverythingFullyScanned && !this.isFinishing && this.selectedCarrier && this.selectedCarrierMethod
                && this.carriers.length && this.carrierMethods.length
            )
        },
        dispatchRequestLines() {
            return this.dispatchRequest.dispatch_request_lines
                .map(line => {
                    line.scanned = (typeof this.scannedIdentifiers[line.product_identifier] !== 'undefined')
                        ? this.scannedIdentifiers[line.product_identifier]
                        : 0

                    line.unit_price = price(line.sales_order_line.original_line_gross_price / line.sales_order_line.quantity)
                    return line
                })
        },
        isEverythingFullyScanned() {
            return !this.dispatchRequestLines
                .filter(line => {
                    return line.scanned !== line.quantity
                })
                .length
        },
        selectedCarrierData() {
            const carrier = this.carriers.find(carrier => carrier.identifier === this.selectedCarrier)
            return carrier ? carrier : {carrier_methods: []}
        },
        selectedCarrierMethodData() {
            const method = this.selectedCarrierData.carrier_methods
                .find(method => method.identifier === this.selectedCarrierMethod)

            return method ? method : {}
        },
        carrierMethods() {
            try {
                return this.selectedCarrierData.carrier_methods
                    .filter(carrierMethod => {
                        return (carrierMethod.is_active && carrierMethod.is_active_for_location)
                            || carrierMethod.identifier === this.dispatchRequest.carrier_method_identifier
                    })
                    .sort((a,b) => {
                        let compA = a.display_name.toLowerCase()
                        let compB = b.display_name.toLowerCase()
                        if (compA > compB) return 1
                        if (compA < compB) return -1
                        return 0
                    })
            }
            catch (error) {
                // console.error(error)
                return []
            }
        },
        isOriginalCarrierMethodOverruleable() {
            try {
                return this.carriers
                    .filter(carrier => carrier.identifier === this.dispatchRequest.carrier_method.carrier.identifier)
                    [0].carrier_methods
                    .filter(carrierMethod => carrierMethod.identifier === this.dispatchRequest.carrier_method.identifier)
                    [0].can_be_overruled_by_dispatcher

            } catch (e) {
                console.warn(e)
                return false
            }
        },
        billingAddress() {
            return this.dispatchRequest.sales_order.addresses
                .find(address => address.address_type === 'billing')
        },
        isSwitchCarrierEnabled() {
            if (!this.isOriginalCarrierMethodOverruleable || !this.hasPermission('orders.dispatchrequests.changecarrier')) {
                return false
            }
            
            return !this.isOnlyOneCarrierAvailable || !this.isOnlyOneCarrierMethodAvailable
        },
    },

    async mounted() {
        this.unableToShipSelectedReason = Object.keys(this.unableToShipReasons)[0]
        await this.loadDispatchRequest()
            .then(async () => {
                let promises = []
                this.dispatchRequest?.dispatch_request_lines?.forEach(line => promises.push(this.$store.getters.getProductInfo(line.product_identifier)))
                return Promise.all(promises)
            })

        this.$store.getters.getConfig
            .then(() => {
                this.unableToShipReasons = {}

                _.each(this.$store.state.config.settings['frontend-parameters.process-products-sort']['unable-to-ship-reasons'], (reason) => {
                    this.unableToShipReasons[reason.description] = reason.description
                })
            })
    },

    methods: {
        async loadDispatchRequest() {
            let relations = [
                'sales_order.addresses',
                'dispatch_request_lines.sales_order_line',
                'carrier_method.carrier',
                'dispatch_type',
                'tracking_codes',
                'dispatcher_enabled_cups_printer_types',
                'box_suggestion.suggested_box',
                'dispatch_request_batch',
            ]

            return this.$http.get(`/api/rest/sales-orders/${this.salesOrder.identifier}/dispatch-requests/${this.dispatchRequestId}?include=${relations.join(',')}`)
                .then(response => {
                    this.dispatchRequest = response.data
                    if (this.dispatchRequest.status !== 'requested') {
                        throw new Error()
                    }

                    this.loadCarriers()
                        .then(() => {
                            this.$refs.scanInput.focus()
                        })
                        .catch(e => {})
                        .finally(() => this.isLoading = false)
                })
                .catch(() => {
                    this.$emit('dataChanged')
                    this.$router.push({
                        name: 'OrderDetailOverview',
                        params: {
                            salesOrderIdentifier: this.salesOrder.identifier,
                        },
                    })
                })
        },
        loadCarriers() {
            return this.$http.get(`/api/rest/locations/${this.dispatchRequest.dispatcher_type}/${this.dispatchRequest.dispatcher_identifier}/dispatch-types`)
                .then(response => {
                    this.carriers = response.data
                        .find(dispatchType => dispatchType.id === this.dispatchRequest.dispatch_type_id)
                        .carriers
                        .filter(carrier => carrier.carrier_methods.some(method => method.is_active && method.is_active_for_location))

                    this.selectedCarrier = this.dispatchRequest.carrier ? this.dispatchRequest.carrier_method.carrier.identifier : ''
                    this.selectedCarrierMethod = this.dispatchRequest.carrier_method ? this.dispatchRequest.carrier_method.identifier : ''

                    if (!this.selectedCarrierMethod) {
                        this.$alert('Fout bij het ophalen van de bezorgwijze. Neem contact op met OmniStock.', 'danger', false)
                    }
                })
                .catch(e => {
                    console.error(e)
                    this.$alert('Fout bij het ophalen van vervoersmethodes', 'danger', false)
                    throw e
                })
        },
        scan() {
            if (!this.scanIdentifier.length) {
                return
            }

            this.scanIdentifier = this.scanIdentifier.padStart(13, '0')
            this.isScannedIdentifierAlreadyComplete = false
            this.lastScanIdentifier = this.scanIdentifier
            this.isScannedBarcodeNotFound = false
            let isBarcodeFound = false

            _.each(this.dispatchRequestLines, line => {
                if (!line.sales_order_line.ean_codes) {
                    line.sales_order_line.ean_codes = ''
                }

                /**
                 * @see https://omnistock-nl.atlassian.net/browse/OMS-57
                 * LET OP: In principe willen we hier ook over naar het DRQLine->ean_codes veld. Echter wordt deze pagina alleen door Intertoys gebruikt.
                 * En om niks stuk te maken laten we het voorlopig even zo. Ook omdat we op termijn mogelijk deze pagina geheel laten vervangen door het algemene ProcessProduct-proces.
                 */
                let eans = line.sales_order_line.ean_codes.toString().split(' ')
                if (eans.indexOf(this.scanIdentifier) === -1) {
                    return
                }

                isBarcodeFound = true
                if (line.quantity === line.scanned) {
                    this.$alert(`Product met barcode "${this.lastScanIdentifier}" is al voltooid`, 'danger')
                    return
                }

                if (typeof this.scannedIdentifiers[line.product_identifier] === 'undefined') {
                    this.$set(this.scannedIdentifiers, line.product_identifier, 0);
                }

                this.scannedIdentifiers[line.product_identifier]++
                this.$alert(`Product gescand!`, 'info')
            })

            if (!isBarcodeFound) {
                this.$alert(`Geen product met barcode "${this.lastScanIdentifier}" in deze order`, 'danger')
            }

            this.scanIdentifier = ''
            this.$refs.scanInput.focus()
        },
        changeCarrier() {
            if (this.selectedCarrierMethod === this.dispatchRequest.carrier_method.identifier) {
                return
            }

            this.isSavingCarrier = true
            this.$http.post(`/api/rest/dispatch-requests`, [{
                status: this.dispatchRequest.status,
                dispatch_type_identifier: this.dispatchRequest.dispatch_type.identifier,
                dispatcher_identifier: this.dispatchRequest.dispatcher_identifier,
                dispatcher_type: this.dispatchRequest.dispatcher_type,
                dispatch_date: this.today,
                carrier_identifier: this.selectedCarrierData.identifier,
                carrier_method_identifier: this.selectedCarrierMethodData.identifier,
                lines: this.dispatchRequest.dispatch_request_lines.map(line => {
                    return {
                        old_line_ids: [line.id],
                        quantity: line.quantity,
                    }
                })
            }])
                .then(response => {
                    this.isLoading = true
                    this.$alert(`De vervoerder en/of bezorgwijze is aangepast. Vergeet niet om een nieuw vervoerslabel aan te vragen!`, 'info', false)
                    this.dispatchRequestId = response.data[0].id
                    this.$router.replace({
                        name: 'OrderPickByStoreScan',
                        params: {
                            salesOrderIdentifier: this.salesOrder.identifier,
                            dispatchRequestId: response.data[0].id
                        },
                    })

                    this.$emit('dataChanged')
                    this.isSavingCarrier = false
                    this.$bvModal.hide('switchCarrierModal')
                })
                .catch(e => {
                    this.$alert(`Fout bij het wijzigen van de vervoerder en/of bezorgwijze`, 'danger')
                    this.isSavingCarrier = false
                })
        },
        processOrder(status, postData, successMessage) {
            this.isFinishing = true
            return this.$http.put(`/api/rest/sales-orders/${this.salesOrder.identifier}/dispatch-requests/${this.dispatchRequestId}/pick-by-store/${status}`, postData)
                .then(() => {
                    this.$alert(successMessage)
                    this.$router.push({
                        name: 'OrderDetailOverview',
                        params: {
                            salesOrderIdentifier: this.salesOrder.identifier,
                        },
                    })
                    this.$emit('dataChanged')
                })
                .catch(e => {
                    this.$alert(`Fout bij het voltooien van de levering`, 'danger')
                })
                .then(() => this.isFinishing = false)
        },
        ship() {
            this.processOrder('shipped', {},`De status van de levering is gewijzigd naar 'Klaar voor verzending'.`)
        },
        unableToShip() {
            this.processOrder(
                'unable-to-ship',
                {reason: this.unableToShipSelectedReason},
                `De levering is gemarkeerd als manco`
            )
        },
        getProductImage(productIdentifier) {
            return this.$store.state.products[productIdentifier]?.attribute_values
                ?.find(value => value.product_attribute.identifier === 'image_url')
                ?.value
        },
        showBoxSuggestion(dispatchRequest) {
            return dispatchRequest.box_suggestion
                && dispatchRequest.box_suggestion.suggested_box
                && this.hasPermission('orders.dispatchrequests.boxsuggestions');
        },
        async startNewScanProcess() {
            if (this.dispatchRequest.dispatch_request_batch_id) {
                this.$router.push({
                    name: 'ProcessStartWithBatch',
                    params: {
                        locationTypeIdentifier: this.dispatchRequest.dispatcher_type,
                        locationIdentifier: this.dispatchRequest.dispatcher_identifier,
                        batchModelType: 'shipment-batch',
                        batchModelIdentifier: this.dispatchRequest.dispatch_request_batch?.identifier,
                        processType: 'sort',
                    },
                })
                return;
            }

            this.isCreatingBatch = true;
            let response = await this.$http.get(`/api/rest/locations/${this.dispatchRequest.dispatcher_type}/${this.dispatchRequest.dispatcher_identifier}/storage-locations?type=sort`)

            if (!response.data?.length) {
                this.$alert('Fout bij het aanmaken van batch. Geen sorteerzone gevonden. Neem contact op met de beheerder.', 'danger')
                return
            }

            this.$http.post(`/api/rest/locations/${this.dispatchRequest.dispatcher_type}/${this.dispatchRequest.dispatcher_identifier}/dispatch-request-batches`, {
                sorting_zone_identifier: response.data?.[0]?.identifier,
                dispatch_date: today,
                status: 'sorting',
                batch_type: 'default',
                is_sortable: false, // 'true' is alleen nodig voor het DC
                dispatch_requests: [
                    this.dispatchRequest.identifier,
                ],
            })
                .then(response => {
                    this.$router.push({
                        name: 'ProcessStartWithBatch',
                        params: {
                            locationTypeIdentifier: this.dispatchRequest.dispatcher_type,
                            locationIdentifier: this.dispatchRequest.dispatcher_identifier,
                            batchModelType: 'shipment-batch',
                            batchModelIdentifier: response.data.identifier,
                            processType: 'sort',
                        },
                    })
                })
                .catch(e => {
                    console.error(e)
                    this.$alert('Fout bij het starten van de verwerking', 'danger')
                })
                .finally(() => this.isCreatingBatch = false)
        },
    },

    watch: {
        carrierMethods(val) {
            // When selecting a carrier that has only one method: auto select it
            if (this.isSwitchCarrierEnabled && val.length === 1) {
                this.selectedCarrierMethod = val[0].identifier
            }
        },
    },
}
</script>

<style lang="scss" scoped>
span.iconCheckmark {
    display: inline-block;
    height: 24px;
    width: 24px;
    background-color: #0f870f;
    border-radius: 24px;
    line-height: 27px;
    color: #FFF;
    font-size: 16px;
    text-align: center;
}

table.order-lines {
    th, td {
        padding: 0.75rem 0.5rem;

        .product-image {
            cursor: pointer;
            max-width: 80px;
            max-height: 80px;
        }
    }
}
.carrier-icon {
    max-width: 50px;
    max-height: 23px;
    filter: grayscale(0.2);

    &:hover {
        filter: grayscale(0) !important;
    }
}
</style>
